import { FC } from 'react';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { PageHeader } from 'components/page-header';
import { DataTable } from 'components/data-table';
import { Link as CarbonLink } from '@carbon/react';
import { format0 } from 'utils/number';
import { StatsBar } from 'components/stats-bar';
import { nonIntersectingExplicitAssignmentsLoadableAtom } from './atoms';
import type { NonIntersectingExplicitAssignment } from './types';
import styles from './page.module.scss';

const columnHelper = createColumnHelper<NonIntersectingExplicitAssignment>();

const columns = [
  columnHelper.accessor('unitWellAssignmentId', {
    id: 'unitWellAssignmentId',
    header: 'Unit Well Assignment ID',
    size: 200,
    meta: { type: 'text' },
  }),
  columnHelper.accessor('developmentAreaName', {
    id: 'developmentAreaName',
    header: 'Unit',
    size: 260,
    cell: (info) => {
      const row = info.row.original;
      return (
        <CarbonLink
          href={row.developmentAreaHref}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          {info.getValue()}
        </CarbonLink>
      );
    },
  }),
  columnHelper.accessor('wellUWI', {
    id: 'wellUWI',
    header: 'Well UWI',
    size: 220,
    cell: (info) => {
      const row = info.row.original;
      return (
        <CarbonLink
          href={row.wellHref}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          {info.getValue()}
        </CarbonLink>
      );
    },
  }),
  columnHelper.accessor('distanceFt', {
    id: 'distanceFt',
    header: 'Distance (ft)',
    size: 140,
    cell: (info) => {
      const value = info.getValue();
      return value !== null ? format0(value) : '—';
    },
  }),
];

export const NonIntersectingExplicitAssignmentsPage: FC = () => {
  const loadable = useAtomValue(nonIntersectingExplicitAssignmentsLoadableAtom);

  const isLoading = loadable.state === 'loading';
  const error =
    loadable.state === 'hasError'
      ? loadable.error instanceof Error
        ? loadable.error.message
        : 'Failed to fetch data'
      : null;
  const rows = loadable.state === 'hasData' ? (loadable.data?.data ?? []) : [];
  const total =
    loadable.state === 'hasData' ? (loadable.data?.stats.total ?? 0) : 0;

  if (error) {
    return (
      <>
        <PageHeader
          breadcrumbs={[]}
          title="Non-Intersecting Explicit Assignments"
          actions={null}
        />
        <div className={styles.error}>Error: {error}</div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Non-Intersecting Explicit Assignments"
        actions={null}
      />

      <div className={styles.sectionHeader}>
        <div className={styles.sectionDescription}>
          Explicit unit well assignments where the effective well geometry
          (survey perforated lateral with fallback to estimated producing
          segment) does not intersect the assigned development area geometry.
        </div>
        <div className={styles.expectedState}>
          Expected: Empty - explicit assignments should spatially intersect
          their assigned development area
        </div>
      </div>

      <StatsBar stats={[{ label: 'Total', value: total, tagType: 'red' }]} />

      <DataTable
        data={rows}
        columns={columns as ColumnDef<NonIntersectingExplicitAssignment>[]}
        getRowId={(row) => String(row.unitWellAssignmentId)}
        isLoading={isLoading}
        error={null}
        persistenceKey="non-intersecting-explicit-assignments"
        config={{
          sorting: true,
          globalSearch: true,
          columnFilters: false,
          columnResizing: true,
          columnReordering: true,
          columnPinning: true,
          columnVisibility: true,
          export: {
            exportPresets: [
              {
                name: 'Export',
                format: 'csv',
                filename: 'non-intersecting-explicit-assignments',
              },
            ],
          },
        }}
      />
    </>
  );
};
